import React, { useRef, useState, useEffect } from "react";
import { Folder, Check, X, Upload, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import InfiniteScroll from "react-infinite-scroll-component";
import FileItemPicker from "../FileItemPicker/FileItemPicker";
import "./FilePickerDialog.css";
import { FilesystemNode } from "../../api/listFolderContents/types";
import FilePickerHeader from "../FilePickerHeader/FilePickerHeader";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { fetchFileManagerMetadata } from "../../slices/fileManagerMetadataSlice";
import { mapFileTypesWithIcons } from "../../utils/fileTypeIcons";
import FileDetailsPanel from "../FileDetailsPanel/FileDetailsPanel";
import TagSidebar from "../TagSidebar/TagSidebar";
import { resetFileManagementState } from "../../slices/fileManagementSlice";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

// Still import these for now, as we're not wiring the actual filtering yet
import { fileTypes, tags } from "../../data/filterData";
import FilePickerFilterSidebar from "@/modules/hub/features/FileManagement/components/FilePickerFilterSidebar/FilePickerFilterSidebar";
import { useFilePickerManagement } from "@/modules/hub/features/FileManagement/hooks/useFilePickerManagement";
import { ENABLE_ALGOLIA_FILES } from "@/modules/hub/features/FileManagement/services/fileSearchService";
import MacStyleUploadIndicator from "@/modules/hub/features/FileManagement/components/MacStyleUploadIndicator/MacStyleUploadIndicator";
import MacStyleDeleteIndicator from "@/modules/hub/features/FileManagement/components/MacStyleDeleteIndicator/MacStyleDeleteIndicator";
import { useNotification } from "@/context/NotificationContext";
import FilePickerLoadingSkeleton from "@/modules/hub/features/FileManagement/components/FilePickerLoadingSkeleton/FilePickerLoadingSkeleton";
import { batchConvertFilesToPublic } from "../../api/batchConvertFiles/batchConvertFilesApi";
import { useBulkDeleteFiles } from "../../hooks/useBulkDeleteFiles";
import BulkDeleteAlertDialog from "../BulkDeleteAlertDialog/BulkDeleteAlertDialog";
import { downloadAndSaveMultipleNodes } from "../../api/downloadMultipleNodes/downloadMultipleNodesApi";

interface FilePickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (
    files: Array<{
      fileUuid: string;
      fileUrl: string;
      publicUrl: string;
      name: string;
    }>,
  ) => void;
  allowedFileTypes?: string[]; // Optional filter for specific file types
  title?: string;
  multiSelect?: boolean; // New prop for multiple selection
  mode?: "picker" | "manager"; // New prop for dual mode functionality
}

export default function FilePickerDialog({
  isOpen,
  onClose,
  onSelect,
  allowedFileTypes,
  multiSelect = false,
  mode = "picker",
}: FilePickerDialogProps) {
  const fileManagerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isFullScreenSidebar, setIsFullScreenSidebar] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FilesystemNode[]>([]);
  const [detailsNode, setDetailsNode] = useState<FilesystemNode | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedForBulk, setSelectedForBulk] = useState<Set<string>>(
    new Set(),
  );
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);
  const [nodeToReplace, setNodeToReplace] = useState<FilesystemNode | null>(
    null,
  );
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [isTagSidebarOpen, setIsTagSidebarOpen] = useState(false);
  const [tagNode, setTagNode] = useState<FilesystemNode | null>(null);
  const dispatch = useAppDispatch();
  const [isMobile, setIsMobile] = useState(false);
  const [isLoadingPresignedUrls, setIsLoadingPresignedUrls] = useState(false);
  const { showNotification } = useNotification();
  const [isRecursiveSearch, setIsRecursiveSearch] = useState(false);
  const [showDeleteIndicator, setShowDeleteIndicator] = useState(false);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  // Track whether sidebar state was changed by user (manual toggle)
  const [sidebarManuallyToggled, setSidebarManuallyToggled] = useState(false);

  // Mac window control states
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Get metadata from Redux store
  const {
    tags: apiTags,
    fileTypes: apiFileTypes,
    loading: metadataLoading,
  } = useAppSelector((state: RootState) => state.fileManagerMetadata);

  // Get Algolia facets from Redux store
  const { algoliaFacets, isUsingAlgolia } = useAppSelector(
    (state: RootState) => state.fileManagement,
  );

  // Check if we're on mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      // Auto-hide sidebar on mobile
      if (mobile && isSidebarOpen && !isFullScreenSidebar) {
        setIsSidebarOpen(false);
        setSidebarManuallyToggled(false); // Reset manual toggle state
      } else if (!mobile && !isSidebarOpen && !sidebarManuallyToggled) {
        // Only auto-show sidebar on desktop if user hasn't manually toggled it
        setIsSidebarOpen(true);
        setIsFullScreenSidebar(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, isFullScreenSidebar, sidebarManuallyToggled]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Reset component state
      setSelectedFiles([]);
      setDetailsNode(null);
      setIsDetailsOpen(false);
      setIsTagSidebarOpen(false);
      setTagNode(null);
      setIsFullscreen(false);
      setSidebarManuallyToggled(false);
      setSelectedFileTypes([]);
      setSelectedTags([]);
      setIsRecursiveSearch(false);
      setIsSelectionMode(false);
      setSelectedForBulk(new Set());

      // Reset Redux state
      dispatch(resetFileManagementState());
    }
  }, [isOpen, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Reset Redux state when component unmounts
      dispatch(resetFileManagementState());
    };
  }, [dispatch]);

  // Map API file types to the format expected by the sidebar
  const fileTypesWithIcons = mapFileTypesWithIcons(apiFileTypes);

  // Prepare data for the filter sidebar based on whether we're using Algolia
  const getFilterData = () => {
    if (isUsingAlgolia && algoliaFacets) {
      // Transform Algolia facets to match the sidebar format
      const facetFileTypes = algoliaFacets.fileType
        ? Object.entries(algoliaFacets.fileType).map(([type, count]) => ({
            id: type,
            name: type,
            count,
            icon: null, // Icons can be added based on type
          }))
        : [];

      // Create properly typed Algolia tags
      interface AlgoliaTag {
        id: string;
        name: string;
        count: number;
        color: string;
      }

      const facetTags: AlgoliaTag[] = algoliaFacets["tags.name"]
        ? Object.entries(algoliaFacets["tags.name"]).map(([name, count]) => ({
            id: name, // Use name as ID for Algolia facets
            name,
            count,
            color: "#6b7280", // Default gray color
          }))
        : [];

      return {
        fileTypes: facetFileTypes,
        tags: facetTags,
      };
    }

    // Fall back to metadata API data (non-Algolia mode)
    return {
      fileTypes: fileTypesWithIcons.length > 0 ? fileTypesWithIcons : fileTypes,
      tags: apiTags.length > 0 ? apiTags : tags,
    };
  };

  const filterData = getFilterData();

  // Filter state
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]); // Changed from number[] to string[] for tag names

  // Helper functions for filter UI
  const toggleFileType = (typeId: string) => {
    setSelectedFileTypes((prev) =>
      prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId],
    );
  };

  const toggleTag = (tagIdOrName: string | number) => {
    // Convert to string for Algolia mode
    const tagName = String(tagIdOrName);
    setSelectedTags((prev) =>
      prev.includes(tagName)
        ? prev.filter((name) => name !== tagName)
        : [...prev, tagName],
    );
  };

  // Count active filters for badge
  const activeFiltersCount = selectedFileTypes.length + selectedTags.length;

  const {
    // State
    folderItems,
    currentFolder,
    breadcrumbs,
    searchInput,
    filterType,
    hasMore,
    viewMode,
    isInitialLoading,
    canNavigateBack,
    canNavigateForward,
    uploadProgress,
    isDeleteDialogOpen,
    selectedNode,

    // Setters
    setViewMode,
    setUploadProgress,
    setIsDeleteDialogOpen,

    // Handlers
    handleNavigateToFolder,
    handleNavigateBack,
    handleNavigateForward,
    fetchMoreItems,
    handleSearch,
    handleSortChange,
    handleFilterChange,
    handleClearFilters,
    renderSortIcon,
    handleCreateFolder,
    handleRenameNode,
    handleUploadFiles,
    handleUpdateNodeAccess,
    handleOpenDeleteDialog,
    handleDeleteNode,
    handleReplaceFile,
  } = useFilePickerManagement({
    selectedFileTypes,
    selectedTags,
    isOpen,
    recursive: isRecursiveSearch,
  });

  // Bulk delete functionality using the new hook
  const {
    isBulkDeleteDialogOpen,
    deleteJob,
    isBulkDeleting,
    handleOpenBulkDeleteDialog,
    handleCloseBulkDeleteDialog,
    handleBulkDelete,
  } = useBulkDeleteFiles({
    refreshFolder: () => {
      // Refresh current folder after bulk delete
      handleNavigateToFolder(currentFolder?.uuid || null);
    },
  });

  // Show Mac-style delete indicator when there's an active delete job
  useEffect(() => {
    if (deleteJob) {
      setShowDeleteIndicator(true);
    }
  }, [deleteJob]);

  // Handle closing the delete indicator
  const handleCloseDeleteIndicator = () => {
    setShowDeleteIndicator(false);
    // Clear the delete job state after a short delay to allow animation
    setTimeout(() => {
      handleCloseBulkDeleteDialog();
    }, 300);
  };

  // Bulk download handler
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const handleBulkDownload = async () => {
    if (selectedForBulk.size === 0) return;

    try {
      setIsBulkDownloading(true);
      const uuids = Array.from(selectedForBulk);
      await downloadAndSaveMultipleNodes(uuids);
      // Success notification removed - download already provides browser feedback
    } catch (error) {
      console.error("Error downloading files:", error);
      showNotification("Error", "Failed to download files", "error");
    } finally {
      setIsBulkDownloading(false);
    }
  };

  // Fetch metadata when component mounts, folder changes, or recursive search changes
  // Only fetch metadata API if not using Algolia (Algolia provides facets directly)
  useEffect(() => {
    if (isOpen && !ENABLE_ALGOLIA_FILES) {
      dispatch(
        fetchFileManagerMetadata(
          currentFolder?.uuid || null,
          isRecursiveSearch,
        ),
      );
    }
  }, [dispatch, isOpen, currentFolder, isRecursiveSearch]);

  // Get current folder name for the hint
  const currentFolderName =
    currentFolder?.name ||
    (breadcrumbs.length > 0
      ? breadcrumbs[breadcrumbs.length - 1].name
      : "this folder");

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy"; // Show the copy icon when dragging
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleUploadFiles(files);
      e.dataTransfer.clearData();
    }
  };

  // Handle "New Folder" action
  const handleCreateNewFolder = () => {
    // Call the create folder function without a name - backend will generate "Untitled Folder"
    handleCreateFolder();
  };

  // Handle file upload with native file picker
  const handleUploadFilesAction = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection from native file picker
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleUploadFiles(files);
      // Reset the input value so the same file can be selected again
      e.target.value = "";
    }
  };

  const clearAllFilters = () => {
    setSelectedFileTypes([]);
    setSelectedTags([]);
    handleClearFilters();
  };

  const handleItemClick = (item: FilesystemNode) => {
    // In selection mode, clicking toggles bulk selection
    if (isSelectionMode) {
      toggleBulkSelection(item.uuid);
      return;
    }

    // Normal file selection behavior when not in selection mode
    if (item.type === "file") {
      // Check if this file type is allowed
      const isAllowed = isFileAllowed(item);
      if (!isAllowed) return; // Don't select files that aren't allowed

      if (multiSelect) {
        // For multi-select, toggle the item in the array
        setSelectedFiles((prev) => {
          const isSelected = prev.some((file) => file.uuid === item.uuid);
          if (isSelected) {
            return prev.filter((file) => file.uuid !== item.uuid);
          } else {
            return [...prev, item];
          }
        });
      } else {
        // For single select, replace the selection
        const isCurrentlySelected =
          selectedFiles.length === 1 && selectedFiles[0].uuid === item.uuid;
        setSelectedFiles(isCurrentlySelected ? [] : [item]);
      }
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      // Clear selections when exiting selection mode
      setSelectedForBulk(new Set());
    }
  };

  const toggleBulkSelection = (uuid: string) => {
    setSelectedForBulk((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uuid)) {
        newSet.delete(uuid);
      } else {
        newSet.add(uuid);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    const allSelectableUuids = folderItems
      .filter((item) => item.type === "file" || item.type === "folder")
      .map((item) => item.uuid);
    setSelectedForBulk(new Set(allSelectableUuids));
  };

  const deselectAll = () => {
    setSelectedForBulk(new Set());
  };

  // Helper function to check if a file is allowed based on its mimeType
  const isFileAllowed = (item: FilesystemNode): boolean => {
    // Folders are always allowed
    if (item.type === "folder") return true;

    // If no file type restrictions, everything is allowed
    if (!allowedFileTypes || allowedFileTypes.length === 0) return true;

    // Check if the file's mime type matches any allowed type
    return allowedFileTypes.some((allowedType) => {
      // If the allowed type ends with *, do a prefix match
      if (allowedType.endsWith("*")) {
        const prefix = allowedType.slice(0, -1);
        return item.mimeType?.startsWith(prefix) || false;
      }
      // Otherwise do an exact match
      return item.mimeType === allowedType;
    });
  };

  const handleConfirmSelection = async () => {
    if (selectedFiles.length === 0) return;

    // Check if any selected files are private - they must be converted to public
    const privateFiles = selectedFiles.filter((file) => !file.isPublic);
    if (privateFiles.length > 0) {
      // Use native browser confirm dialog
      const fileCount = privateFiles.length;
      const fileWord = fileCount === 1 ? "file" : "files";
      const message =
        `${fileCount} ${fileWord} must be converted to public before they can be used.\n\n` +
        `Files to convert:\n${privateFiles
          .slice(0, 5)
          .map((f) => `• ${f.name}`)
          .join("\n")}` +
        `${privateFiles.length > 5 ? `\n• and ${privateFiles.length - 5} more...` : ""}\n\n` +
        `Do you want to make these files public?`;

      if (window.confirm(message)) {
        // Convert the files
        await handleConvertFiles(privateFiles.map((f) => f.uuid));
      }
      return;
    }

    // All files are public, return them with their public URLs
    const filesData = selectedFiles.map((file) => ({
      fileUuid: file.uuid || "",
      fileUrl: file.url || "",
      publicUrl: file.publicUrl || "",
      name: file.name || "",
    }));

    // Call the onSelect callback with the public files
    onSelect(filesData);
    onClose();
  };

  const handleConvertFiles = async (fileUuids: string[]) => {
    try {
      setIsLoadingPresignedUrls(true);

      // Convert files to public
      const conversionResult = await batchConvertFilesToPublic(fileUuids);

      // Update the selected files with new public status and URLs
      const updatedFiles = selectedFiles.map((file) => {
        const convertedFile = conversionResult.files.find(
          (f) => f.uuid === file.uuid,
        );
        if (convertedFile) {
          return {
            ...file,
            isPublic: convertedFile.isPublic,
            publicUrl: convertedFile.publicUrl || file.publicUrl,
          };
        }
        return file;
      });

      setSelectedFiles(updatedFiles);

      // Return the files with their public URLs
      const filesData = updatedFiles.map((file) => ({
        fileUuid: file.uuid || "",
        fileUrl: file.url || "",
        publicUrl: file.publicUrl || "",
        name: file.name || "",
      }));

      // Immediately call onSelect and onClose
      onSelect(filesData);
      onClose();

      showNotification(
        "Success",
        `${conversionResult.totalConverted} file(s) successfully converted to public`,
        "success",
      );
    } catch (error) {
      console.error("Error converting files:", error);
      showNotification(
        "Error",
        "Failed to convert files. Please try again.",
        "error",
      );
    } finally {
      setIsLoadingPresignedUrls(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    setSidebarManuallyToggled(true); // Mark as manually toggled
    setIsFullScreenSidebar(false); // Reset full-screen mode when toggling
  };

  const openSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(true);
      setIsFullScreenSidebar(true);
    } else {
      setIsSidebarOpen(true);
      setIsFullScreenSidebar(false);
    }
    setSidebarManuallyToggled(true); // Mark as manually toggled
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setIsFullScreenSidebar(false);
    setSidebarManuallyToggled(true); // Mark as manually toggled
  };

  const handleShowDetails = (item: FilesystemNode) => {
    setDetailsNode(item);
    setIsDetailsOpen(true);
  };

  const handleItemUpdate = (updatedItem: FilesystemNode) => {
    // Update the Redux store with the new access status
    // Default to false if isPublic is undefined (folders don't have this property)
    handleUpdateNodeAccess(
      updatedItem.uuid,
      updatedItem.isPublic ?? false,
      updatedItem.publicUrl,
    );

    // Update selected files if the updated item is selected
    setSelectedFiles((prevSelected) =>
      prevSelected.map((file) =>
        file.uuid === updatedItem.uuid ? updatedItem : file,
      ),
    );

    // Update details node if it's the same item
    if (detailsNode?.uuid === updatedItem.uuid) {
      setDetailsNode(updatedItem);
    }
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
  };

  const handleOpenReplaceDialog = (item: FilesystemNode) => {
    setNodeToReplace(item);
    setIsReplaceDialogOpen(true);
    setReplaceFile(null);
  };

  const handleConfirmReplace = async () => {
    if (nodeToReplace && replaceFile) {
      await handleReplaceFile(nodeToReplace.uuid, replaceFile);
      setIsReplaceDialogOpen(false);
      setNodeToReplace(null);
      setReplaceFile(null);
    }
  };

  const handleOpenTagSidebar = (item: FilesystemNode) => {
    setTagNode(item);
    setIsTagSidebarOpen(true);
    setIsDetailsOpen(false); // Close details if open
  };

  const handleCloseTagSidebar = () => {
    setIsTagSidebarOpen(false);
  };

  const handleTagNodeUpdate = (updatedNode: FilesystemNode) => {
    // Update the item in the list
    handleItemUpdate(updatedNode);
    // Update the tag node reference
    setTagNode(updatedNode);
  };

  // Handlers for Mac window controls
  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Apply classes based on Mac window control states
  const dialogClasses = `max-w-6xl p-0 !bg-white dark:!bg-white dark:!border-gray-200 ${isFullscreen ? "fullscreen-dialog" : ""}`;

  // Calculate height for content area based on fullscreen mode and mode type
  const contentStyle =
    mode === "manager"
      ? isFullscreen
        ? { height: "calc(100vh - 20px)" } // Manager mode, fullscreen - no footer
        : { height: "calc(100vh - 90px)" } // Manager mode, regular - no footer
      : isFullscreen
        ? { height: "calc(100vh - 70px)" } // Picker mode, fullscreen - with footer
        : { height: "calc(100vh - 140px)" }; // Picker mode, regular - with footer

  return (
    <Dialog onOpenChange={onClose} open={isOpen}>
      {/* Added Mac-style controls and classes for fullscreen */}
      <DialogContent className={dialogClasses} hideCloseButton>
        <div className="h-full flex flex-col overflow-hidden sm:rounded-lg">
          {/* Content wrapper with sidebar and main content */}
          <div
            className="flex flex-row relative file-manager-container"
            style={contentStyle}
          >
            {/* Backdrop for full-screen sidebar (mobile only) */}
            {isFullScreenSidebar && (
              <div className="sidebar-backdrop" onClick={closeSidebar} />
            )}

            {/* Filter Sidebar - only shown when isSidebarOpen is true */}
            {isSidebarOpen && (
              <div
                className={`mobile-sidebar-container ${isFullScreenSidebar ? "fullscreen-sidebar" : ""}`}
              >
                <FilePickerFilterSidebar
                  activeFiltersCount={activeFiltersCount}
                  clearAllFilters={clearAllFilters}
                  fileTypes={filterData.fileTypes}
                  filterType={filterType}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  isLoading={metadataLoading}
                  isOpen={true} // Always open when rendered
                  isRecursive={isRecursiveSearch}
                  onClose={closeSidebar}
                  onToggleRecursive={setIsRecursiveSearch}
                  searchInput={searchInput}
                  selectedFileTypes={selectedFileTypes}
                  selectedTags={selectedTags}
                  tags={filterData.tags}
                  toggleFileType={toggleFileType}
                  toggleTag={toggleTag}
                />
              </div>
            )}

            {/* Main Content with scrollable area - Always rendered, just hidden by overlay when in full-screen mode */}
            <div
              className={`flex-1 min-w-0 flex flex-col relative ${isDragging ? "finder-drop-target" : ""}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              ref={fileManagerRef}
            >
              {/* Finder-style drop hint - shown when dragging */}
              {isDragging && (
                <div className="finder-drop-hint">
                  <Upload className="finder-drop-hint-icon" size={24} />
                  <span>Drop to upload to {currentFolderName}</span>
                </div>
              )}

              {/* Header stays outside scrollable area - updated with new props */}
              <FilePickerHeader
                activeFiltersCount={activeFiltersCount}
                breadcrumbs={breadcrumbs}
                canNavigateBack={canNavigateBack}
                canNavigateForward={canNavigateForward}
                handleSearch={handleSearch}
                isBulkDownloading={isBulkDownloading}
                isFullscreen={isFullscreen}
                isRecursive={isRecursiveSearch}
                isSelectionMode={isSelectionMode}
                isSidebarOpen={isSidebarOpen}
                onBulkDelete={() => {
                  // Confirm before starting bulk delete
                  if (selectedForBulk.size > 0) {
                    const itemCount = selectedForBulk.size;
                    const message = `Are you sure you want to delete ${itemCount} item${itemCount !== 1 ? 's' : ''}? This action cannot be undone.`;

                    if (window.confirm(message)) {
                      handleBulkDelete(Array.from(selectedForBulk));
                      // Clear selection and exit selection mode
                      setSelectedForBulk(new Set());
                      setIsSelectionMode(false);
                    }
                  }
                }}
                onBulkDownload={handleBulkDownload}
                onClose={onClose}
                onCreateFolder={handleCreateNewFolder}
                onDeselectAll={deselectAll}
                onNavigateBack={handleNavigateBack}
                onNavigateBreadcrumb={handleNavigateToFolder}
                onNavigateForward={handleNavigateForward}
                onOpenSidebar={openSidebar}
                onSelectAll={selectAll}
                onToggleFullscreen={handleToggleFullscreen}
                onToggleSelectionMode={toggleSelectionMode}
                onToggleSidebar={toggleSidebar}
                onUploadFiles={handleUploadFilesAction}
                searchInput={searchInput}
                selectedCount={selectedForBulk.size}
                totalCount={folderItems.length}
                setViewMode={setViewMode}
                /*title={title}*/
                viewMode={viewMode}
              />

              {/* Hidden file input for native file picker */}
              <input
                className="hidden"
                multiple
                onChange={handleFileInputChange}
                ref={fileInputRef}
                type="file"
              />

              {/* Scrollable container for infinite scroll */}
              <div
                className="overflow-auto flex-1 p-4"
                id="scrollableContainer"
                ref={scrollContainerRef}
              >
                {/* Sort controls for list view */}
                {viewMode === "list" && (
                  <div className="file-picker-horizontal-scroll">
                    <div className="file-list-header mb-2 border-b border-gray-200 dark:border-gray-700 pb-2 hidden md:flex mt-4 file-list-content">
                      <div className="flex-1 flex items-center gap-2">
                        <button
                          className="text-sm font-medium flex items-center gap-1"
                          onClick={() => handleSortChange("name")}
                        >
                          Name{renderSortIcon("name")}
                        </button>
                      </div>
                      <div className="w-24 text-sm font-medium flex items-center gap-1">
                        <button
                          className="text-sm font-medium flex items-center gap-1"
                          onClick={() => handleSortChange("fileType")}
                        >
                          Type{renderSortIcon("fileType")}
                        </button>
                      </div>
                      <div className="w-32 text-sm font-medium flex items-center gap-1">
                        <button
                          className="text-sm font-medium flex items-center gap-1"
                          onClick={() => handleSortChange("updatedAt")}
                        >
                          Modified{renderSortIcon("updatedAt")}
                        </button>
                      </div>
                      <div className="w-24 text-sm font-medium">Size</div>
                    </div>
                  </div>
                )}

                {isInitialLoading ? (
                  <FilePickerLoadingSkeleton count={20} />
                ) : (
                  <div
                    className={
                      viewMode === "list" ? "file-picker-horizontal-scroll" : ""
                    }
                  >
                    <InfiniteScroll
                      dataLength={folderItems.length}
                      endMessage={
                        <div className="text-center py-4">
                          <p className="text-gray-500 dark:text-gray-400">
                            {folderItems.length > 0
                              ? "No more items to load"
                              : ""}
                          </p>
                        </div>
                      }
                      hasMore={hasMore}
                      loader={
                        <div className="text-center py-4">
                          <p className="text-gray-500 dark:text-gray-400">
                            Loading more...
                          </p>
                        </div>
                      }
                      next={fetchMoreItems}
                      scrollableTarget="scrollableContainer"
                    >
                      {folderItems.length > 0 ? (
                        <div
                          className={
                            viewMode === "grid"
                              ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4"
                              : "flex flex-col gap-1 mt-4 file-list-content"
                          }
                        >
                          {folderItems.map((item) => (
                            <div
                              className={`fade-in ${item.type === "folder" ? "folder-item" : ""}`}
                              key={item.uuid}
                            >
                              <FileItemPicker
                                allowedFileTypes={allowedFileTypes}
                                isBulkSelected={selectedForBulk.has(item.uuid)}
                                isSelected={selectedFiles.some(
                                  (file) => file.uuid === item.uuid,
                                )}
                                isSelectionMode={isSelectionMode}
                                item={item}
                                onClick={() => handleItemClick(item)}
                                onDelete={() => handleOpenDeleteDialog(item)}
                                onFolderClick={handleNavigateToFolder}
                                onItemUpdate={handleItemUpdate}
                                onManageTags={() => handleOpenTagSidebar(item)}
                                onRename={handleRenameNode}
                                onReplaceFile={() =>
                                  handleOpenReplaceDialog(item)
                                }
                                onShowDetails={handleShowDetails}
                                viewMode={viewMode}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Folder
                            className="mx-auto mb-4 text-gray-400"
                            size={48}
                          />
                          <p className="text-gray-500 dark:text-gray-400 text-lg">
                            {searchInput || activeFiltersCount > 0
                              ? isRecursiveSearch
                                ? "No items match your filters in this folder or its subfolders."
                                : "No items match your filters in this folder."
                              : "This folder is empty."}
                          </p>
                          {(searchInput || activeFiltersCount > 0) && (
                            <button
                              className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
                              onClick={clearAllFilters}
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>
                      )}
                    </InfiniteScroll>
                  </div>
                )}
              </div>
            </div>

            {/* Details Panel - As a third column in the layout */}
            {(!isMobile || (isMobile && isDetailsOpen)) && (
              <FileDetailsPanel
                isMobile={isMobile}
                isOpen={isDetailsOpen}
                node={detailsNode}
                onClose={closeDetails}
              />
            )}

            {/* Tag Sidebar - As a third column in the layout */}
            {(!isMobile || (isMobile && isTagSidebarOpen)) && (
              <TagSidebar
                isMobile={isMobile}
                isOpen={isTagSidebarOpen}
                node={tagNode}
                onClose={handleCloseTagSidebar}
                onNodeUpdate={handleTagNodeUpdate}
              />
            )}
          </div>

          {/* Footer with cancel/select buttons - Only rendered in picker mode */}
          {mode === "picker" && (
            <DialogFooter className="px-6 py-4 border-t fixed-height-footer">
              <div className="flex items-center justify-between w-full">
                <div>
                  {selectedFiles.length > 0 && (
                    <div className="text-sm text-gray-500">
                      {multiSelect
                        ? `Selected: ${selectedFiles.length} file${selectedFiles.length !== 1 ? "s" : ""}`
                        : `Selected: ${selectedFiles[0].name}`}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={onClose} variant="outline">
                    <X className="mr-2 h-4 w-4" /> Cancel
                  </Button>
                  <Button
                    disabled={
                      selectedFiles.length === 0 || isLoadingPresignedUrls
                    }
                    onClick={handleConfirmSelection}
                  >
                    {isLoadingPresignedUrls ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                        Loading...
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" /> Select
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          )}

          {/* Upload Progress Indicator */}
          <MacStyleUploadIndicator
            setUploadProgress={setUploadProgress}
            uploadProgress={uploadProgress}
          />

          {/* Delete Progress Indicator (Mac-style) */}
          {showDeleteIndicator && deleteJob && (
            <MacStyleDeleteIndicator
              deleteJob={deleteJob}
              hasUploadIndicator={uploadProgress.length > 0}
              onClose={handleCloseDeleteIndicator}
            />
          )}

          {/* Delete Confirmation Dialog */}
          <AlertDialog
            onOpenChange={setIsDeleteDialogOpen}
            open={isDeleteDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Delete {selectedNode?.type === "folder" ? "Folder" : "File"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete{" "}
                  <strong>"{selectedNode?.name}"</strong>? This action cannot be
                  undone.
                  {selectedNode?.type === "folder" && (
                    <span className="block mt-2 text-amber-600 dark:text-amber-400">
                      ⚠️ You can only delete empty folders. Make sure this
                      folder does not contain any files or subfolders.
                    </span>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  onClick={handleDeleteNode}
                >
                  Delete {selectedNode?.type === "folder" ? "Folder" : "File"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Replace File Dialog */}
          <AlertDialog
            onOpenChange={setIsReplaceDialogOpen}
            open={isReplaceDialogOpen}
          >
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5" />
                  Replace File
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-4">
                  <div className="text-sm">
                    <span className="font-medium">Current file:</span>{" "}
                    {nodeToReplace?.name}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Select replacement file:
                    </label>
                    <Input
                      className="cursor-pointer"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setReplaceFile(file);
                        }
                      }}
                      type="file"
                    />
                    {replaceFile && (
                      <div className="text-sm text-muted-foreground">
                        Selected: {replaceFile.name} (
                        {(replaceFile.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    setReplaceFile(null);
                    setNodeToReplace(null);
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-blue-600 hover:bg-blue-700 focus:ring-blue-600"
                  disabled={!replaceFile}
                  onClick={handleConfirmReplace}
                >
                  Replace File
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Bulk Delete Dialog - Commented out in favor of Mac-style indicator */}
          {/* <BulkDeleteAlertDialog
            deleteJob={deleteJob}
            isOpen={isBulkDeleteDialogOpen}
            itemCount={selectedForBulk.size}
            onClose={handleCloseBulkDeleteDialog}
            onConfirm={() => handleBulkDelete(Array.from(selectedForBulk))}
          /> */}
        </div>
      </DialogContent>
    </Dialog>
  );
}
